import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Leaf, ArrowLeft, Sparkles, Clock, Users, Check, Lock, Calendar, Utensils, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SUITE_NAME } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramDetailPage = () => {
  const navigate = useNavigate();
  const { programId } = useParams();
  const { user, token } = useAuth();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchProgram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const fetchProgram = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/programs/${programId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgram(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access restricted');
        navigate('/myinsul');
      } else if (error.response?.status === 404) {
        toast.error('Program not found');
        navigate('/myinsul');
      } else {
        toast.error('Failed to load program');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/myinsul/subscribe/${programId}`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription to this program? You will lose access to program content.')) return;
    setCancelling(true);
    try {
      await axios.post(
        `${API_URL}/api/myinsul/subscription/${program.subscription.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription cancelled. A confirmation email is on its way.');
      fetchProgram();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!program) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/myinsul')}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-lg">{SUITE_NAME}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Program Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8"
        >
          {/* Hero Image */}
          <div className="h-64 bg-gradient-to-br from-emerald-400 to-teal-500 relative">
            {program.image_url ? (
              <img 
                src={program.image_url} 
                alt={program.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Leaf className="w-24 h-24 text-white/30" />
              </div>
            )}
            {program.is_subscribed && (
              <div className="absolute top-4 right-4 bg-emerald-500 text-white font-bold px-4 py-2 rounded-full flex items-center gap-2">
                <Check className="w-5 h-5" />
                Subscribed
              </div>
            )}
          </div>

          {/* Program Info */}
          <div className="p-8">
            <h1 className="text-3xl font-bold mb-4">{program.name}</h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2 bg-emerald-50 px-3 py-2 rounded-full">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span>{program.duration_weeks} weeks</span>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-full">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span>Weekly meal plans</span>
              </div>
              {program.subscriber_count > 0 && (
                <div className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-full">
                  <Users className="w-4 h-4 text-purple-600" />
                  <span>{program.subscriber_count} enrolled</span>
                </div>
              )}
            </div>

            <p className="text-muted-foreground mb-8 leading-relaxed">
              {program.description}
            </p>

            {/* Pricing & CTA */}
            {!program.is_subscribed && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-primary">${program.price_monthly}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Cancel anytime</p>
                </div>
                <Button 
                  size="lg"
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="rounded-full px-8 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  data-testid="subscribe-button"
                >
                  {subscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-5 h-5 mr-2" />
                  )}
                  {subscribing ? 'Processing...' : 'Subscribe Now'}
                </Button>
              </div>
            )}

            {program.is_subscribed && program.subscription && (
              <div className="p-6 bg-emerald-50 rounded-2xl">
                <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
                  <Check className="w-5 h-5" />
                  You're subscribed to this program
                </div>
                <p className="text-sm text-emerald-600">
                  Currently on Week {program.subscription.current_week} of {program.duration_weeks}
                </p>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="mt-4 rounded-full"
                  variant="outline"
                >
                  View in Dashboard
                </Button>
                <Button
                  data-testid="cancel-subscription-btn"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  variant="outline"
                  className="mt-4 ml-3 rounded-full border-red-300 text-red-600 hover:bg-red-50"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Cancel subscription
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Week Overview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl p-8"
        >
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Program Schedule
          </h2>

          <div className="space-y-4">
            {program.weeks?.map((week, index) => {
              const isUnlocked = program.is_subscribed && 
                program.subscription?.unlocked_weeks?.includes(week.week_number);
              const isCurrent = program.is_subscribed && 
                program.subscription?.current_week === week.week_number;
              
              return (
                <div 
                  key={week.week_number}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isCurrent 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : isUnlocked
                        ? 'border-emerald-200 bg-white hover:bg-emerald-50'
                        : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        isCurrent
                          ? 'bg-emerald-500 text-white'
                          : isUnlocked
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-gray-200 text-gray-500'
                      }`}>
                        {week.week_number}
                      </div>
                      <div>
                        <h3 className={`font-semibold ${!isUnlocked && !program.is_subscribed ? '' : ''}`}>
                          {week.title}
                        </h3>
                        {week.description && (
                          <p className="text-sm text-muted-foreground">{week.description}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      {isCurrent && (
                        <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          Current
                        </span>
                      )}
                      {!isUnlocked && !isCurrent && (
                        <Lock className="w-5 h-5 text-gray-400" />
                      )}
                      {isUnlocked && !isCurrent && (
                        <Check className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  </div>

                  {/* Show meal preview for unlocked weeks */}
                  {isUnlocked && week.meals && week.meals.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-emerald-200">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Utensils className="w-4 h-4" />
                        <span>{week.meals.length} meals planned</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!program.is_subscribed && (
            <div className="mt-6 p-4 bg-amber-50 rounded-xl text-center">
              <Lock className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="text-sm text-amber-700">
                Subscribe to unlock weekly meal plans and recipes
              </p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ProgramDetailPage;
